import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Layout } from '@/components/layout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useToast } from '@/lib/hooks/useToast'
import { Loader2, CheckCircle2, XCircle } from 'lucide-react'

/**
 * Azure OAuth2 Callback Page
 *
 * This page handles the redirect from Azure AD after user authentication.
 * The backend /api/v1/auth/azure/callback endpoint processes the authorization code,
 * validates the user, assigns roles, and sets JWT cookies before redirecting here.
 *
 * Flow:
 * 1. Azure redirects to backend /api/v1/auth/azure/callback?code=...&state=...
 * 2. Backend validates code, creates/updates user, assigns roles, issues JWT tokens
 * 3. Backend redirects to this frontend page (with success/error params if needed)
 * 4. This page shows loading state while tokens are being set
 * 5. Redirects to home page where CrisisGuard handles routing
 *
 * Note: The actual OAuth flow is handled server-side to keep client secrets secure.
 */
export function AzureCallbackPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const hasProcessed = useRef(false)

  useEffect(() => {
    // Prevent infinite loops by only processing once
    if (hasProcessed.current) return
    hasProcessed.current = true

    // Check URL params for error or success indicators
    const params = new URLSearchParams(window.location.search)
    const error = params.get('error')
    const message = params.get('message')
    const isNewUser = params.get('is_new_user')

    if (error) {
      // Error from Azure or backend
      setStatus('error')

      // Check if this is a provisioning error
      if (message === 'auth.userNotProvisioned' && isNewUser !== null) {
        // Store provisioning error in sessionStorage and redirect to login
        const provisioningError = {
          isNewUser: isNewUser === 'true',
          message,
        }
        sessionStorage.setItem('provisioningError', JSON.stringify(provisioningError))

        // Redirect to login immediately
        setTimeout(() => {
          window.location.href = '/login'
        }, 500)
        return
      }

      // Handle translation keys with interpolation
      let errorDescription = message || t('auth.azureCallbackError', 'Authentication failed. Please try again.')

      // If message is a translation key, translate it
      if (message && message.startsWith('auth.')) {
        // Check if the error details contain tenant_id for interpolation
        const detailsMatch = message.match(/auth\.(\w+)/)
        if (detailsMatch) {
          // Extract any additional query params that might be interpolation values
          const tenantId = params.get('tenant_id')
          if (tenantId && message === 'auth.azureTenantNotWhitelisted') {
            errorDescription = t(message, { tenantId })
          } else {
            errorDescription = t(message, message)
          }
        }
      }

      setErrorMessage(errorDescription)
      toast({
        title: t('auth.authenticationFailed', 'Authentication Failed'),
        description: errorDescription,
        variant: 'destructive',
      })
    } else {
      // Success - backend has set JWT cookies
      // Show success briefly then redirect
      setStatus('success')
      toast({
        title: t('auth.loginSuccess', 'Login Successful'),
        description: t('auth.azureLoginSuccess', 'Successfully signed in with Microsoft'),
        variant: 'success',
      })

      // Redirect to home - CrisisGuard will handle routing to dashboard or crisis select
      setTimeout(() => {
        window.location.href = '/'
      }, 1500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout showNavigation={false}>
      <div className="h-full flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          <Card variant="elevated" className="animate-fade-in glass-card dark:glass-card">
            <CardHeader className="text-center space-y-3">
              <div className="flex justify-center">
                {status === 'loading' && (
                  <Loader2 className="h-12 w-12 text-primary animate-spin" />
                )}
                {status === 'success' && (
                  <CheckCircle2 className="h-12 w-12 text-success animate-bounce" />
                )}
                {status === 'error' && <XCircle className="h-12 w-12 text-destructive" />}
              </div>
              <CardTitle className="text-2xl">
                {status === 'loading' &&
                  t('auth.azureCallbackLoading', 'Completing Sign In...')}
                {status === 'success' && t('auth.azureCallbackSuccess', 'Sign In Successful!')}
                {status === 'error' && t('auth.azureCallbackErrorTitle', 'Sign In Failed')}
              </CardTitle>
              <CardDescription className="text-base">
                {status === 'loading' &&
                  t(
                    'auth.azureCallbackLoadingDescription',
                    'Please wait while we complete your authentication...'
                  )}
                {status === 'success' &&
                  t('auth.azureCallbackSuccessDescription', 'Redirecting you to the application...')}
                {status === 'error' && (errorMessage || t('auth.azureCallbackErrorDescription', 'An error occurred during authentication.'))}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              {status === 'loading' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="h-2 w-2 bg-primary rounded-full animate-pulse" />
                    <div className="h-2 w-2 bg-primary rounded-full animate-pulse [animation-delay:200ms]" />
                    <div className="h-2 w-2 bg-primary rounded-full animate-pulse [animation-delay:400ms]" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('auth.verifyingCredentials', 'Verifying your credentials...')}
                  </p>
                </div>
              )}
              {status === 'success' && (
                <p className="text-sm text-muted-foreground">
                  {t('auth.redirectingShortly', 'You will be redirected shortly...')}
                </p>
              )}
              {status === 'error' && (
                <Button
                  onClick={() => window.location.href = '/login'}
                  variant="gradient"
                  size="lg"
                  className="w-full"
                >
                  {t('auth.backToLogin', 'Back to Login')}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  )
}
